import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Trophy, Calendar, User, Building2, BarChart3, Euro,
  Menu as MenuIcon, Search, MessageSquare, Users, HelpCircle, Shield,
  LogOut, Sun, Moon, Settings
} from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

const playerPrimary = [
  { to: '/dashboard', icon: Home, label: 'Home' },
  { to: '/leagues', icon: Trophy, label: 'Leghe' },
  { to: '/matches', icon: Calendar, label: 'Partite' },
  { to: '/profile', icon: User, label: 'Profilo' },
];

const playerSecondary = [
  { to: '/facilities', icon: Building2, label: 'Strutture', desc: 'Trova e prenota un campo' },
  { to: '/search', icon: Search, label: 'Cerca', desc: 'Partite e annunci pubblici' },
  { to: '/announcements', icon: MessageSquare, label: 'Bacheca', desc: 'Trova compagni o squadre' },
  { to: '/referees', icon: Users, label: 'Arbitri', desc: 'Cerca un arbitro disponibile' },
  { to: '/help', icon: HelpCircle, label: 'Aiuto', desc: 'Guide e FAQ' },
];

const getFacilityPrimary = (facilityId) => [
  { to: `/facilities/${facilityId}/dashboard`, icon: BarChart3, label: 'Dashboard' },
  { to: `/facilities/${facilityId}/dashboard#bookings`, icon: Calendar, label: 'Prenotaz.' },
  { to: `/facilities/${facilityId}/dashboard#payments`, icon: Euro, label: 'Incassi' },
  { to: `/facilities/${facilityId}`, icon: Building2, label: 'Struttura' },
];

const getFacilitySecondary = (facilityId) => [
  { to: '/profile', icon: User, label: 'Profilo', desc: 'Account personale' },
  { to: `/facilities/${facilityId}/edit`, icon: Settings, label: 'Impostazioni', desc: 'Modifica struttura' },
  { to: '/help', icon: HelpCircle, label: 'Aiuto', desc: 'Guide per gestori' },
];

const MoreSheet = ({ open, onOpenChange, secondary, user, isAdmin, onLogout, theme, toggleTheme }) => {
  const navigate = useNavigate();
  const go = (path) => {
    onOpenChange(false);
    setTimeout(() => navigate(path), 50);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>

        {/* User card */}
        {user && (
          <button
            type="button"
            onClick={() => go('/profile')}
            className="mt-4 w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-left hover:shadow-lg transition-shadow"
          >
            <Avatar className="h-11 w-11 ring-2 ring-white/40 shrink-0">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-white/20 text-white text-sm">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{user.name}</p>
              <p className="text-xs text-white/85 truncate">Apri profilo</p>
            </div>
          </button>
        )}

        {/* Secondary nav */}
        <div className="mt-4 grid gap-1.5">
          {secondary.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => go(item.to)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/60 active:bg-muted transition-colors text-left"
              >
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-tight">{item.label}</p>
                  {item.desc && (
                    <p className="text-[11px] text-muted-foreground truncate">{item.desc}</p>
                  )}
                </div>
              </button>
            );
          })}

          {isAdmin && (
            <button
              type="button"
              onClick={() => go('/admin')}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/60 active:bg-muted transition-colors text-left"
            >
              <div className="h-9 w-9 rounded-lg bg-rose-500/15 text-rose-600 flex items-center justify-center shrink-0">
                <Shield className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium leading-tight">Admin</p>
                <p className="text-[11px] text-muted-foreground">Pannello amministratore</p>
              </div>
            </button>
          )}
        </div>

        {/* Theme + logout */}
        <div className="mt-4 pt-4 border-t grid gap-2">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => { toggleTheme(); }}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 mr-3" /> : <Moon className="h-4 w-4 mr-3" />}
            Tema {theme === 'dark' ? 'chiaro' : 'scuro'}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={async () => {
              onOpenChange(false);
              await onLogout();
            }}
          >
            <LogOut className="h-4 w-4 mr-3" />
            Esci
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const BottomNav = () => {
  const { user, facilityData, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isFacilityOwner = user?.account_type === 'facility' && facilityData?.facility_id;
  const primary = isFacilityOwner
    ? getFacilityPrimary(facilityData.facility_id)
    : playerPrimary;
  const secondary = isFacilityOwner
    ? getFacilitySecondary(facilityData.facility_id)
    : playerSecondary;

  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Match secondary entries to mark "Menu" active when user is on one of those paths
  const isOnSecondary = secondary.some(s => location.pathname === s.to)
    || (isAdmin && location.pathname === '/admin');

  return (
    <>
      <nav
        className="safe-bottom-nav fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50 md:hidden"
        data-testid="bottom-nav"
      >
        <div className="flex justify-around items-center h-16">
          {primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(
                "flex flex-col items-center justify-center tap-target gap-1 transition-colors flex-1",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs font-medium">{item.label}</span>
            </NavLink>
          ))}

          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center tap-target gap-1 transition-colors flex-1",
              isOnSecondary
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
            data-testid="nav-menu"
            aria-label="Apri menu"
          >
            <MenuIcon className="h-5 w-5" />
            <span className="text-xs font-medium">Menu</span>
          </button>
        </div>
      </nav>

      <MoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        secondary={secondary}
        user={user}
        isAdmin={isAdmin && !isFacilityOwner}
        onLogout={handleLogout}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    </>
  );
};

export default BottomNav;
